import { BodyText } from "@/components/ui/Text";
import { useAVCallSuggestedConditions } from "../../network";
import { AVCallSuggestedCondition } from "./AVCallSuggestedCondition";

interface AVCallSuggestedConditionsProps {
  patientId: string;
  encounterId?: string;
}

/**
 * Renders AI-extracted condition suggestions from an AV call at the top of
 * the Medical History "Active" tab. Returns null (renders nothing) when:
 *   - there's no active encounter,
 *   - the post-call processing workflow hasn't produced suggestions yet, or
 *   - the LLM extracted nothing meaningful from the transcript.
 *
 * v1 surfaces only `historicalConditions`. `rfeConditions` are extracted and
 * persisted by the backend but intentionally hidden in this UI.
 */
export const AVCallSuggestedConditions = ({
  patientId,
  encounterId,
}: AVCallSuggestedConditionsProps) => {
  const { data } = useAVCallSuggestedConditions({ patientId, encounterId });

  if (!encounterId) return null;

  const historicalConditions = data?.historicalConditions ?? [];
  if (historicalConditions.length === 0) return null;

  return (
    <div className="flex flex-col gap-2 mb-3 pb-3 border-b border-default">
      <BodyText size="xs" textColor="tertiary" className="uppercase">
        Suggested from this visit ({historicalConditions.length})
      </BodyText>
      {historicalConditions.map((condition, idx) => (
        <AVCallSuggestedCondition
          key={`${condition.sourceId ?? "src"}-${condition.term ?? "term"}-${idx}`}
          condition={condition}
          patientId={patientId}
          encounterId={encounterId}
        />
      ))}
    </div>
  );
};
